/**
 * Shopify Admin URL Helpers
 *
 * Safely constructs Shopify admin URLs with multi-layer security:
 *
 * Security Model:
 * 1. Input source: Data comes from RLS-protected database (organizations.shopify_domain)
 * 2. Regex validation: Only allows alphanumeric + hyphens (no special chars)
 * 3. Length cap: Max 50 chars prevents DoS via oversized inputs
 * 4. Integer validation: Order IDs must be positive integers
 * 5. URL encoding: encodeURIComponent as defense-in-depth
 * 6. Null safety: Invalid inputs return null — links don't render rather than pointing to the wrong store
 *
 * @module shopify-urls
 */

const SHOPIFY_SLUG_PATTERN = /^[a-z0-9-]+$/i;
const MAX_SLUG_LENGTH = 50;

/**
 * Extracts and validates store slug from Shopify domain.
 *
 * @param shopifyDomain - Organization's Shopify domain (e.g., "threadheads.myshopify.com")
 * @returns Validated store slug or null if invalid/missing
 *
 * @example
 * getShopifyStoreSlug("threadheads-custom-printing.myshopify.com") // "threadheads-custom-printing"
 * getShopifyStoreSlug(null) // null
 * getShopifyStoreSlug("invalid!@#.myshopify.com") // null
 */
export function getShopifyStoreSlug(shopifyDomain: string | null | undefined): string | null {
  if (!shopifyDomain) return null;

  // Extract store name using split for explicit parsing
  // (e.g., "threadheads" from "threadheads.myshopify.com")
  // Note: split always returns at least one element, so [0] is safe
  const slug = (shopifyDomain.split('.myshopify.com')[0] ?? '').toLowerCase();

  // Length cap to prevent DoS via oversized inputs
  if (slug.length > MAX_SLUG_LENGTH) {
    console.warn('Shopify domain slug too long', { shopifyDomain });
    return null;
  }

  // Validate: alphanumeric + hyphens only
  if (!SHOPIFY_SLUG_PATTERN.test(slug)) {
    console.warn('Invalid Shopify domain format', { shopifyDomain });
    return null;
  }

  return slug;
}

/**
 * Builds Shopify admin order URL with full validation.
 *
 * @param shopifyDomain - Organization's Shopify domain
 * @param shopifyOrderId - Numeric Shopify order ID (must be positive integer)
 * @returns Validated admin URL or null if inputs invalid
 *
 * @example
 * getShopifyAdminOrderUrl("threadheads.myshopify.com", 12345)
 * // "https://admin.shopify.com/store/threadheads/orders/12345"
 *
 * getShopifyAdminOrderUrl(null, -1) // null
 */
export function getShopifyAdminOrderUrl(
  shopifyDomain: string | null | undefined,
  shopifyOrderId: number | null | undefined
): string | null {
  // Validate order ID is a positive integer
  if (!shopifyOrderId || !Number.isInteger(shopifyOrderId) || shopifyOrderId <= 0) {
    return null;
  }

  const slug = getShopifyStoreSlug(shopifyDomain);
  if (!slug) return null;
  return `https://admin.shopify.com/store/${encodeURIComponent(slug)}/orders/${shopifyOrderId}`;
}

/**
 * Builds Shopify admin product URL with full validation.
 *
 * @param shopifyDomain - Organization's Shopify domain
 * @param shopifyProductId - Numeric Shopify product ID (must be positive integer)
 * @returns Validated admin URL or null if inputs invalid
 */
export function getShopifyAdminProductUrl(
  shopifyDomain: string | null | undefined,
  shopifyProductId: number | null | undefined
): string | null {
  if (!shopifyProductId || !Number.isInteger(shopifyProductId) || shopifyProductId <= 0) {
    return null;
  }

  const slug = getShopifyStoreSlug(shopifyDomain);
  if (!slug) return null;
  return `https://admin.shopify.com/store/${encodeURIComponent(slug)}/products/${shopifyProductId}`;
}

/**
 * Builds a Shopify admin product-VARIANT URL, with graceful degradation.
 *
 * Reuses {@link getShopifyAdminProductUrl} for validation (domain + product id).
 * When the variant id is missing/invalid, returns the product URL (still useful);
 * when the product id/domain is invalid, returns null (link won't render).
 *
 * @example
 * getShopifyAdminVariantUrl("threadheads.myshopify.com", 7890, 12345)
 * // "https://admin.shopify.com/store/threadheads/products/7890/variants/12345"
 */
export function getShopifyAdminVariantUrl(
  shopifyDomain: string | null | undefined,
  shopifyProductId: number | null | undefined,
  shopifyVariantId: number | null | undefined
): string | null {
  const productUrl = getShopifyAdminProductUrl(shopifyDomain, shopifyProductId);
  if (!productUrl) return null;
  if (!shopifyVariantId || !Number.isInteger(shopifyVariantId) || shopifyVariantId <= 0) {
    return productUrl;
  }
  return `${productUrl}/variants/${shopifyVariantId}`;
}
